import React, { Suspense, useState, useRef, useEffect, lazy } from 'react';
import { useStyles } from '@wix/tpa-settings/dist/src/hooks';

import { useTranslation } from 'react-i18next';
import * as moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
require(`moment/locale/en-gb`);

//hooks
import useWixEventListener from '../../../hooks/useWixEventListener';
import usePrevious from '../../../hooks/usePrevious';
import useEditorType from '../../../hooks/useEditorType';
import useViewPort from '../../../hooks/useViewPort'

//Actions
import {
  handleMonthChangeAction,
  onSettingsUpdate,
  onWidgetLoaded,
  updateEventColor,
  handleTimezoneChangeAciton,
  updateData,
  getEventsForCalendarSelected,
  updateCalendarSelected,
  getRecurrence,
} from '../../settings/actions/widgetActions';

//Handlers
import openMobilePopup from './handlers/openMobilePopup';

import Actions from '@wix/calendar-client-library-v3/dist/es/src/enums/ActionsEnum'

//Utils
import { TimeFormatsEnum, Loader,
  // EmptyState
 } from '@wix/calendar-client-library-v3/dist/es/src';

import { datesMaper } from '../widdgetUtils/widdgetUtils';
import { getErrorMessage } from '../widdgetUtils/errorHandlers';
import { weekStarterArray } from '../widdgetUtils/startWeekArray';
import {
  getLocaleById,
  getLanguagesByVal,
  getLanguagesByLocale,
} from '../../settings/utils/helpFunctions';
import {
  addBiEventPublish,
  addBiEventLiveSite,
} from '../widdgetUtils/biEventsHandlers';

import { updateCalendarList } from '../widdgetUtils/updateCalendarList';

//Components
import LoaderSpiner from '../../Loader/Loader';
import AgendaWrapper from '../calendarsWrappers/agendaWrapper/AgendaResizeWrapper';
import MonthlyWrapper from '../calendarsWrappers/monthlyWrapper/MonthlyResizeWrapper';
import WeeklyWrapper from '../calendarsWrappers/weeklyWrapper/WeeklyWrapper';
import WeeklyWrepperMobile from '../calendarsWrappers/weeklyWrapperMobile/WeeklyWrapperMobile';
import EmptyStateLocale from '../calendarsWrappers/EmptyStateLocale/EmptyStateLocale'

import {
  getCompId,
  getLocale,
  getStyleParams,
  getWixPublicData,
  isEditorViewMode,
  isLiveSiteViewMode,
  isMobileDevice,
  setHeight,
  wixEvents,
} from '../../../utils';

declare global {
  interface Window {
    EXPERIMENTS: any;
  }
}

const experiments = window.EXPERIMENTS;

const setHeightByref = (calendarRef) => {
  setTimeout(() => {
    if (calendarRef && calendarRef.current) {
      setHeight(calendarRef.current.scrollHeight);
    }
  }, 0);
};

const Widget = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const styles = useStyles();
  const { isEditorX } = useEditorType();
  useViewPort()
  const isApiError = useSelector((store) => store.isApiError);
  const isPreLoaded = useSelector((store) => store.isPreLoaded);
  const isConnect = useSelector((store) => store.isConnect);
  const isDemoContent = useSelector((store) => store.isDemoContent);

  const dates = useSelector((store) => store.dates);
  const isDatesReturn = useSelector((store) => store.isDatesReturn);
  const recurrences = useSelector((store) => store.recurrences);
  const calendarsExistArr = useSelector((store) => store.calendarsExistArr);
  const monthExistsArr = useSelector((store) => store.monthExistsArr);
  const monthEventsExistsArr = useSelector(
    (store) => store.monthEventsExistsArr,
  );
  //const experiments = useSelector(store => store.experiments)
  const defaultView = useSelector((store) => store.defaultView); //under
  const lang = useSelector((store) => store.lang);
  const timeFormatProp = useSelector((store) => store.timeFormat);
  const timezone = useSelector((store) => store.timezone);
  const textAlignment = useSelector((store) => store.textAlignment);
  const calendarsList = useSelector((store) => store.calendarsList);
  const recurrenceArr = useSelector((store) => store.recurrences);

  const isIncorrectRatio =
    window.innerWidth - window.innerHeight > 50 || window.innerWidth < 460;
  const alignmentDefault = isMobileDevice() ? 'center' : 'left';

  const alignment =
    textAlignment === 'left'
      ? 'left'
      : textAlignment === 'right'
      ? 'center'
      : alignmentDefault;
  const timeFormat =
    timeFormatProp && timeFormatProp === 1
      ? TimeFormatsEnum.twentyFourHours
      : TimeFormatsEnum.tweleveHours;

  const layoutNumber =
    typeof getStyleParams().numbers.layoutIcons !== 'undefined'
      ? getStyleParams().numbers.layoutIcons
      : 1;

  const startWeekOn =
    typeof getStyleParams().numbers.startWeekOn !== 'undefined'
      ? getStyleParams().numbers.startWeekOn
      : 0; // 0 ='sunday'

  const browserTimezone: string = Intl.DateTimeFormat()
    .resolvedOptions()
    .timeZone.trim();

  let startWeekOnDay;
  if (startWeekOn === 0) {
    startWeekOnDay = 'sunday';
  }
  if (startWeekOn === 1) {
    startWeekOnDay = 'monday';
  }
  if (startWeekOn === 6) {
    startWeekOnDay = 'saturday';
  }
  const [state, setState] = useState({
    isPrivateEventsShown: true,
    weekStarter: startWeekOnDay,
    isTimeShown: true,
    isTimeZoneShown: true,
    isTodayButtonStyleSeconday: true,
    isShowMoreButtonStyleSecondary: true,
    language:
      typeof lang !== 'undefined' && lang !== ''
        ? getLanguagesByLocale(lang)
        : getLanguagesByLocale(getLocale()),
    locale: typeof lang !== 'undefined' && lang ? lang : getLocale(),
    timezone:
      typeof timezone !== 'undefined' && timezone !== 'UTC'
        ? timezone
        : browserTimezone,
    error: '',
    alignment,
    timeFormat: timeFormat ? timeFormat : TimeFormatsEnum.tweleveHours,
    isStretchable:
      (typeof defaultView !== 'undefined' && defaultView === 1) ||
      (typeof defaultView !== 'undefined' &&
        defaultView === 0 &&
        isIncorrectRatio),
    isBorderShown: false,
    newEvents: dates,
    isAgenda: layoutNumber === 2,
    isMonthly: layoutNumber === 1,
    isWeekly: layoutNumber === 3,

    isFixedSize: true,
    action: Actions.NO_ACTION,
    isSiteColors: true,
    isVisitorLocationTimeZone: true,
    viewMode: 'Editor',
    changeMode: false,
    calendarsListSelectedLength: 0,

    privetEventsTitle: '',
    datePickerMobileDate: moment().format(), // by default current date
  });

  const [weeklyTimezone, setWeeklyTimezone] = useState(state.timezone);
  const [isThrottled, setIsThrottled] = useState(false);

  const calendarRef = useRef(null);
  const todayRef = useRef(null);

  const isMobile = isMobileDevice();

  const localeMap = {
    'zh': 'zh-cn',
    'en': 'en-gb',
    'no': 'nb',
  };

  if (state.locale !== 'en') {
    const mapLocale = localeMap[state.locale] || state.locale;
    try {
      require(`moment/locale/${mapLocale}`);
    } catch (error) {
      require(`moment/locale/${localeMap.en}`);
      console.error(error);
    }
  }

  const getPrivateEventTitle = () => {
    getWixPublicData(`privetEventsTitle`, 'APP')
      .then((d: any) => {
        setState((state) => ({
          ...state,
          privetEventsTitle: d.privetEventsTitle,
        }));
      })
      .catch((error) => {
        console.error('Error getting private event title:', error);
      });
  };

  //FIXME
  useEffect(() => {
    dispatch(onWidgetLoaded());
    setHeightByref(calendarRef);
    getPrivateEventTitle();

    if (calendarsList) {
      dispatch(updateCalendarSelected(calendarsList));
    }

    if (isApiError) {
      setState((state) => ({ ...state, error: getErrorMessage(t, 3) }));
    }

    getStyleParams((styleParams) => {
      if (calendarsList && dates) {
        getStyleParams((styleParams: any) => {
          dispatch(updateEventColor(dates, calendarsList, styleParams));
        });
        updateStyle(styleParams);
      }
    });
  }, []);

  useEffect(() => {
    setState((state) => ({ ...state, action: Actions.NO_ACTION }));
  }, [state.action]);
  useEffect(() => {
    if (calendarsList && isEditorViewMode()) {
      dispatch(updateData(updateCalendarList(calendarsList, styles)));
      dispatch(updateCalendarSelected(calendarsList));
    }
  }, [calendarsList]);

  useWixEventListener(wixEvents.PUBLIC_DATA_CHANGED, () =>
    getPrivateEventTitle(),
  );

  useWixEventListener(wixEvents.STYLE_PARAMS_CHANGE, (styleParams) => {
    if (calendarsList && dates && isEditorViewMode()) {
      dispatch(updateEventColor(dates, calendarsList, styleParams));
      dispatch(updateData(updateCalendarList(calendarsList, styles)));
      updateStyle(styleParams);
    }
  });

  useWixEventListener(wixEvents.SITE_PUBLISHED, () => {
    if (calendarsList && isEditorViewMode()) {
      dispatch(updateData(calendarsList));
      addBiEventPublish(state, isConnect, calendarsList);
    }
  });

  useWixEventListener(wixEvents.SITE_SAVED, () => {
    if (calendarsList && isEditorViewMode()) {
      dispatch(updateData(calendarsList));
    }
  });

  useWixEventListener(wixEvents.EDIT_MODE_CHANGE, (event) => {
    if (event.editMode === 'editor') {
      if (calendarRef && calendarRef.current) {
        //@ts-ignore
        calendarRef.current.click();
      }
      setState((state) => ({ ...state, action: Actions.TODAY }));
    }
  });

  useWixEventListener(wixEvents.SETTINGS_UPDATED, (data) => {
    if (getCompId() === data.value.compId) {
      dispatch(onSettingsUpdate(data));
    }
    if (
      data.key === 'calendarSelected' &&
      calendarsExistArr &&
      !calendarsExistArr.includes(data.value.currentCalendarIdSelected)
    ) {
      dispatch(
        getEventsForCalendarSelected(
          dates,
          data.value.currentCalendarIdSelected,
          calendarsExistArr,
        ),
      );
    }
    if (data.key === 'settingsLoaded') {
      updateStyle(data.value.style);
    }

    if (data.key === 'addEventColor') {
      getStyleParams((styleParams) => {
        dispatch(updateEventColor(dates, calendarsList, styleParams));
      });
      data.value = { dates, calendarsList };
    }
  });

  const prevProps = usePrevious({ dates, recurrences });

  useEffect(() => {
    if (
      prevProps &&
      dates &&
      (prevProps.dates !== dates || prevProps?.recurrences !== recurrences)
    ) {
      getStyleParams((styleParams) => {
        dispatch(updateEventColor(dates, calendarsList, styleParams));
        updateStyle(styleParams);

        const newDates = JSON.parse(JSON.stringify(dates));
        datesMaper(newDates, styleParams, recurrences);
        setState((state) => ({ ...state, newEvents: newDates }));
      });
    }
  }, [dates, recurrences]);

  const handleTimezoneChange = (timezone, start) => {
    setWeeklyTimezone(timezone);
    addBiEventLiveSite(
      'change_time_zone',
      state.isAgenda,
      state.isWeekly,
      state.isMonthly,
    );
    dispatch(handleTimezoneChangeAciton(dates, timezone, start));
  };

  const handleMonthChange = (start, end, timezone, isNextClicked) => {
    addBiEventLiveSite(
      'previus_or_next_month',
      state.isAgenda,
      state.isWeekly,
      state.isMonthly,
    );

     if (isThrottled) return;

        setIsThrottled(true);
        setTimeout(() => {
            setIsThrottled(false);
        }, 400);
    
    dispatch(
      handleMonthChangeAction(
        start,
        end,
        timezone,
        isNextClicked,
        dates,
        calendarsList,
        monthExistsArr,
        monthEventsExistsArr,
      ),
    );
  };

  const expandHandlerBi =(expandedData) => {
      if(expandedData && expandedData.recurringEventId && !expandedData.recurrence)
         dispatch(getRecurrence(dates,expandedData.calendarId,expandedData.recurringEventId));

    addBiEventLiveSite(
      'expand_event',
      state.isAgenda,
      state.isWeekly,
      state.isMonthly,
    );
  };

  const updateStyle = (styleVal: any) => {
    const startWeekOn: string[] = weekStarterArray;
    const timezonesNames: string[] = moment.tz.names();
    const alignmentOption = ['left', 'center'];
    const isAgendaforDesktop =
      typeof styleVal.numbers.layoutIcons === 'undefined'
        ? state.isAgenda
        : styleVal.numbers.layoutIcons === 2;
    const isAgendaforMobile =
      typeof styleVal.numbers.mobile_layoutIcons === 'undefined'
        ? state.isAgenda
        : styleVal.numbers.mobile_layoutIcons === 2;
    let calendarsListSelectedLength = 0;

    if (typeof calendarsList !== 'undefined') {
      calendarsList.map((c) => {
        const key = Object.keys(styleVal.booleans).find((key) =>
          key.includes(c.id),
        );
        if (typeof key !== 'string') {
          if (c.selected === true) {
            calendarsListSelectedLength = calendarsListSelectedLength + 1;
          }
          //@ts-ignore
        } else if (key.value === true) {
          calendarsListSelectedLength = calendarsListSelectedLength + 1;
        }
      });
    }

    if (dates) {
      const newDates = JSON.parse(JSON.stringify(dates));
      datesMaper(newDates, styleVal, recurrences);

      const getTimezone = () => {
        let timezone = state.timezone;

        if (styleVal.numbers.timeZone) {
          timezone = timezonesNames[styleVal.numbers.timeZone];
        }

        if (styleVal.numbers.timezoneOptions === 0) {
          timezone = browserTimezone;
        }

        return timezone;
      };

      setState((state) => ({
        ...state,
        calendarsListSelectedLength,
        isAgenda: isMobile ? isAgendaforMobile : isAgendaforDesktop,
        newEvents: newDates,
        isStretchable:
          typeof styleVal.numbers.cellResize === 'undefined'
            ? state.isStretchable
            : styleVal.numbers.cellResize === 1,
        isFixedSize:
          typeof styleVal.numbers.calendarHeight === 'undefined'
            ? state.isFixedSize
            : styleVal.numbers.calendarHeight !== 1,
        timeFormat:
          typeof styleVal.numbers.timeFormat === 'undefined'
            ? state.timeFormat
            : styleVal.numbers.timeFormat === 24
            ? TimeFormatsEnum.twentyFourHours
            : TimeFormatsEnum.tweleveHours,
        alignment: isMobile
          ? 'center'
          : typeof styleVal.numbers.calendarAlignment !== 'undefined'
          ? alignmentOption[styleVal.numbers.calendarAlignment]
          : state.alignment,
        weekStarter:
          typeof styleVal.numbers.startWeekOn !== 'undefined'
            ? startWeekOn[styleVal.numbers.startWeekOn]
            : state.weekStarter,
        isTodayButtonStyleSeconday:
          styleVal.numbers.todayBtnStyles === 1 ||
          styleVal.numbers.todayBtnStyles === 2,
        locale:
          typeof styleVal.numbers.languages !== 'undefined'
            ? getLocaleById(styleVal.numbers.languages)
            : state.locale,
        language:
          typeof styleVal.numbers.languages !== 'undefined'
            ? getLanguagesByVal(styleVal.numbers.languages)
            : state.language,
        isTimeZoneShown: get(
          styleVal,
          'booleans.isTimeZoneShown',
          state.isTimeZoneShown,
        ),
        isPrivateEventsShown: get(
          styleVal,
          'booleans.isPrivateEventsShown',
          state.isPrivateEventsShown,
        ),
        isTimeShown: get(styleVal, 'booleans.isTimeShown', state.isTimeShown),
        isBorderShown: get(
          styleVal,
          'booleans.mobile_monthlyIsBorderShown',
          state.isBorderShown,
        ),
        error: getErrorMessage(t, styleVal.numbers.errorType),
        timezone: getTimezone(),
        isSiteColors: styleVal.numbers.siteOrGoogleCalendarColors !== 1,
        isVisitorLocationTimeZone: styleVal.numbers.timezoneOptions !== 1,
        isShowMoreButtonStyleSecondary:
          styleVal.numbers.loadMoreBtnStyles === 1 ||
          styleVal.numbers.loadMoreBtnStyles === 2,
      }));
    }
  };

  const calendarsProps = {
    isEditorX,
    experiments,
    openPopup: isMobile
      ? (d) => {
          openMobilePopup(d, state, setState);
        }
      : null,
    onEventExpanded: expandHandlerBi,
    handleTimezoneChange,
    handleMonthChange,
    onCopyClick: () =>
      addBiEventLiveSite(
        'copy_to_google_calendar',
        state.isAgenda,
        state.isWeekly,
        state.isMonthly,
      ),
    onAttachmentClick: () =>
      addBiEventLiveSite(
        'attachment',
        state.isAgenda,
        state.isWeekly,
        state.isMonthly,
      ),
    onLocationClick: () =>
      addBiEventLiveSite(
        'location',
        state.isAgenda,
        state.isWeekly,
        state.isMonthly,
      ),
    onTodayClick: () =>
      addBiEventLiveSite(
        'today_button',
        state.isAgenda,
        state.isWeekly,
        state.isMonthly,
      ),
    //refs
    calendarRef,
    todayRef,
    //state
    isFixedSize: state.isAgenda && isMobile ? true : state.isFixedSize,
    timezone: state.isVisitorLocationTimeZone
      ? browserTimezone
      : state.timezone,
    isLiveSite: isLiveSiteViewMode(),
    isDatesReturn,
    timeFormat: state.timeFormat,
    isMobile,
    isBorderShown: state.isBorderShown,
    error: state.error,
    weekStarter: state.weekStarter,
    dates: state.error ? {} : state.newEvents,
    isStretchable: state.isStretchable,
    alignment: state.alignment,
    isTimezoneShown: state.isTimeZoneShown,
    isTimeShown: state.isTimeShown,
    locale: state.locale,
    isTodayButtonStyleSeconday: state.isTodayButtonStyleSeconday,
    action: state.action,
    isNextDisabled: isThrottled,
    isPrevDisabled: isThrottled,
    isShowMoreButtonStyleSecondary: state.isShowMoreButtonStyleSecondary,
    isPreLoaded,
    wixStyles: getStyleParams(),
    privetEventsTitle: state.privetEventsTitle,
    isWeekly: getStyleParams()?.numbers?.layoutIcons === 3,
    isPrivateEventsShown: state.isPrivateEventsShown,
    calendarsList,
    weeklyTimezone,
    datePickerMobileDate: state.datePickerMobileDate,
    recurrenceArr,
  };

  const calendarRenderer = () => {
    const weeklyDesktop = getStyleParams()?.numbers?.layoutIcons === 3;
    const agendaDesktop = getStyleParams()?.numbers?.layoutIcons === 2;
    let monthlyDesktop = getStyleParams()?.numbers?.layoutIcons === 1;

    const weeklyMobile =
      typeof getStyleParams().numbers.mobile_layoutIcons !== 'undefined'
        ? getStyleParams()?.numbers?.mobile_layoutIcons === 3
        : false;

    const agendaMobile = getStyleParams()?.numbers?.mobile_layoutIcons === 2;

    let monthlyMobile = getStyleParams()?.numbers?.mobile_layoutIcons === 1;

    if (!getStyleParams()?.numbers?.layoutIcons) {
      monthlyDesktop = true;
    }
    if (!getStyleParams()?.numbers?.mobile_layoutIcons) {
      monthlyMobile = true;
    }
    if (isMobile) {
      if (agendaMobile) {
        return <AgendaWrapper calendarsProps={calendarsProps} />;
      }
      if (monthlyMobile) {
        return <MonthlyWrapper calendarsProps={calendarsProps} />;
      }
      if (weeklyMobile) {
        return <WeeklyWrepperMobile calendarsProps={calendarsProps} />;
      }
    } else {
      if (agendaDesktop) {
        return <AgendaWrapper calendarsProps={calendarsProps} />;
      }
      if (monthlyDesktop) {
        return <MonthlyWrapper calendarsProps={calendarsProps} />;
      }
      if (weeklyDesktop) {
        return <WeeklyWrapper calendarsProps={calendarsProps} />;
      }
    }
  };

  const localeForLoading = getLocaleById(getStyleParams()?.numbers?.languages);

  const isLiveSite = isLiveSiteViewMode();

  if (isDemoContent && !isEditorViewMode()) {
    // return <EmptyState
    // locale={localeForLoading}
    // isLiveSite={isLiveSite}/>;
    return (<EmptyStateLocale
     locale={localeForLoading}
    isLiveSite={isLiveSite}/>
    )
  }

  return (
    <>
      {!isDatesReturn ? (
        <Loader
          isPreLoaded={isPreLoaded}
          locale={localeForLoading}
          isLiveSite={isLiveSite}
        />
      ) : (
        <Suspense fallback={<LoaderSpiner isVisible={true} />}>
          {calendarRenderer()}
        </Suspense>
      )}
    </>
  );
};

export default Widget;
